/**
 * API Service pour l'historique des notes
 * Permet de consulter les versions, comparer et restaurer les notes
 */

import { ENTREE_TABLE_LABELS } from '../../components/entrees/entreeTableLabels';

// Types pour l'historique
export interface HistoryCollaborator {
    userId: string;
    displayName: string;
}

export interface ChangesSummary {
    titleChanged: boolean;
    textDelta: number;
    clesAdded: string[];
    clesRemoved: string[];
    clesModified: string[];
}

export interface NoteSnapshot {
    title?: string;
    content: {
        text: string;
        JSON: any;
    };
    cles: Array<{
        id: string;
        cle: string;
        rating?: number;
    }>;
    perimetres: Array<{
        id: string;
        name: string;
    }>;
}

export interface NoteHistoryEntry {
    _id: string;
    noteId: string;
    version: number;
    action: 'created' | 'updated' | 'restored' | 'deleted';
    authorId: string;
    authorName: string;
    collaborators: HistoryCollaborator[];
    snapshot: NoteSnapshot;
    changesSummary?: ChangesSummary;
    createdAt: string;
    expiresAt: string;
}

export interface DiffChange {
    value: string;
    added?: boolean;
    removed?: boolean;
    count?: number;
}

export interface VersionComparison {
    versionA: NoteHistoryEntry | null;
    versionB: NoteHistoryEntry | null;
    textDiff: DiffChange[];
    titleDiff: DiffChange[];
    changeDescription: string;
}

const API_BASE = '/api/note-history';

// Token d'authentification
let authToken: string | null = null;

/**
 * Configure le token d'authentification
 */
export function setHistoryAuthToken(token: string) {
    authToken = token;
}

/**
 * Helper pour les requêtes authentifiées
 */
async function fetchWithAuth<T>(url: string, options: RequestInit = {}): Promise<T> {
    if (!authToken) {
        throw new Error('Token d\'authentification non configuré');
    }

    const response = await fetch(url, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            'accesstoken': authToken, // Required for Azure SWA
            ...options.headers
        }
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Erreur inconnue' }));
        throw new Error(error.message || `Erreur HTTP ${response.status}`);
    }

    const result = await response.json();
    return result.data !== undefined ? result.data : result;
}

/**
 * Récupère l'historique d'une note
 * @param noteId ID de la note
 * @param limit Nombre max de versions à récupérer (défaut: 50)
 */
export async function getNoteHistory(
    noteId: string,
    limit: number = 50
): Promise<NoteHistoryEntry[]> {
    return fetchWithAuth(`${API_BASE}/${noteId}?limit=${limit}`);
}

/**
 * Récupère une version spécifique d'une note
 * @param noteId ID de la note
 * @param version Numéro de version
 */
export async function getNoteVersion(
    noteId: string,
    version: number
): Promise<NoteHistoryEntry> {
    return fetchWithAuth(`${API_BASE}/${noteId}/${version}`);
}

/**
 * Compare deux versions d'une note
 * @param noteId ID de la note
 * @param versionA Première version
 * @param versionB Deuxième version
 */
export async function compareVersions(
    noteId: string,
    versionA: number,
    versionB: number
): Promise<VersionComparison> {
    return fetchWithAuth(`${API_BASE}/${noteId}/diff/${versionA}/${versionB}`);
}

/**
 * Restaure une note vers une version précédente
 * @param noteId ID de la note
 * @param version Numéro de version à restaurer
 * @param authorName Nom de l'auteur de la restauration
 */
export async function restoreVersion(
    noteId: string,
    version: number,
    authorName: string
): Promise<{
    note: any; // Type Note
    restoredFromVersion: number;
}> {
    return fetchWithAuth(`${API_BASE}/${noteId}/restore/${version}`, {
        method: 'POST',
        body: JSON.stringify({ authorName })
    });
}

/**
 * Génère une description lisible des changements
 */
export function formatChangesSummary(summary: ChangesSummary | undefined): string {
    if (!summary) return '';

    const parts: string[] = [];

    if (summary.titleChanged) {
        parts.push('Titre modifié');
    }

    if (summary.textDelta !== 0) {
        const sign = summary.textDelta > 0 ? '+' : '';
        parts.push(`${sign}${summary.textDelta} car.`);
    }

    if (summary.clesAdded.length > 0) {
        parts.push(ENTREE_TABLE_LABELS.notes.resumeAjoutes(summary.clesAdded.length));
    }

    if (summary.clesRemoved.length > 0) {
        parts.push(ENTREE_TABLE_LABELS.notes.resumeRetires(summary.clesRemoved.length));
    }

    if (summary.clesModified.length > 0) {
        parts.push(`${summary.clesModified.length} notation(s) modifiée(s)`);
    }

    return parts.join(', ') || 'Aucun changement';
}

/**
 * Formate une date relative (il y a X minutes, etc.)
 */
export function formatRelativeDate(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return 'À l\'instant';
    if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays} jour(s)`;
    
    return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
}

/**
 * Formate le nom de l'action en français
 */
export function formatAction(action: NoteHistoryEntry['action']): string {
    switch (action) {
        case 'created': return 'Création';
        case 'updated': return 'Modification';
        case 'restored': return 'Restauration';
        case 'deleted': return 'Suppression';
        default: return action;
    }
}
